/*
 * Hero Quote Tool. Powers sections/hero-quote-tool.liquid.
 *
 * A buyer pastes part numbers (one per line, optional quantity after a space,
 * comma, or tab) into the "order pad". We parse them, POST the SKUs to the app
 * proxy lookup endpoint (/apps/machines/lookup.json — Shopify products are the
 * source of truth), and render a terminal-styled, editable RFQ table with live
 * price, availability, per-line weight, an LTL freight estimate and a running
 * total. Actions: copy (TSV + HTML), share link, add-all-to-cart, and Get a
 * quote (the same contact dialog + /apps/machines/quote endpoint as the cart).
 *
 * OEM vs Reliant vs aftermarket: alternate-tier variants follow the SKU
 * conventions <SKU>-RS (genuine Metso Reliant value line) and <SKU>-AM
 * (aftermarket), so for every pasted SKU we also look up both twins in the same
 * request. When a twin exists, the row shows a spec toggle (OEM / Reliant /
 * Aftermarket) with live prices, and savings vs OEM roll up into the total.
 * All real data, no mock.
 *
 * Vanilla, no deps. Guarded so it only runs where [data-hqt] exists.
 */
(function () {
  var KG_TO_LB = 2.2046226;

  // Rough client-side shipping estimate. Light orders ship parcel/ground; once
  // they pass the parcel weight ceiling they go LTL freight (lift-gate free).
  // These are estimates only — see the shipping-rates app endpoint for real
  // carrier rates (needs a destination).
  var PARCEL_MAX_LB = 70;
  var PARCEL_BASE = 9;
  var PARCEL_PER_LB = 0.95;
  var PARCEL_MIN = 12;

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function oneLine(s) {
    return String(s == null ? "" : s).replace(/\s+/g, " ").trim();
  }

  // Prices come from the Admin API in the shop's BASE currency. Display them in
  // the currency the shopper picked in the nav (Shopify Markets) by converting
  // with the rate Shopify exposes on `window.Shopify.currency`. Falls back to
  // the base currency (rate 1) when no currency selection is active.
  function currencyCfg() {
    var c = (typeof window !== "undefined" && window.Shopify && window.Shopify.currency) || {};
    var rate = parseFloat(c.rate);
    return {
      code: c.active || "USD",
      rate: isFinite(rate) && rate > 0 ? rate : 1,
    };
  }

  function fmtMoney(n, digits) {
    var cfg = currencyCfg();
    var v = (Number(n) || 0) * cfg.rate;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: cfg.code,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(v);
    } catch (e) {
      return "$" + v.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
    }
  }

  function usd(n) {
    return fmtMoney(n, 2);
  }

  function usd0(n) {
    return fmtMoney(n, 0);
  }

  // Stock variant from the lookup endpoint → availability label + colour class.
  // `col` is the short word for the dedicated availability column, `word` the
  // fuller phrase used in the email/copy export.
  var LEAD = {
    "in-stock": { col: "In stock", word: "In stock", cls: "in" },
    available: { col: "Available", word: "Available", cls: "in" },
    factory: { col: "Factory", word: "Factory order", cls: "factory", tip: "Factory order. Shipping time may vary, please contact support." },
    "out-of-stock": { col: "Sourcing", word: "Sourcing", cls: "na" },
    unknown: { col: "RFQ", word: "Quote on request", cls: "na" },
  };

  function lead(variant) {
    return LEAD[variant] || LEAD.unknown;
  }

  // Active (selected) values for a row honour the spec toggle. r.spec is
  // 'oem' | 'rs' (Reliant) | 'am' (aftermarket); r.rs / r.am hold the twin
  // variant's live data when it exists (null otherwise).
  function actTier(r) { return r.spec === "am" ? r.am : r.spec === "rs" ? r.rs : null; }
  function actPrice(r) { var t = actTier(r); return t ? t.price : r.oemPrice; }
  // A row is "priced" only when it's found AND has a real, positive price. A $0
  // part comes back with priceAmount null (Number(null) -> 0) and is quote-only:
  // show RFQ, never "$0.00", matching the storefront convention (cards hide the
  // price; the PDP shows a "Quote on request" badge).
  function actPriced(r) { return !!r.found && actPrice(r) > 0; }
  function actWeight(r) { var t = actTier(r); return t ? t.weightKg : r.oemWeightKg; }
  function actVariant(r) { var t = actTier(r); return t ? t.variant : r.oemVariant; }
  function actUrl(r) { var t = actTier(r); return t ? t.url : r.url; }
  function actVariantId(r) { var t = actTier(r); return t ? t.variantId : r.oemVariantId; }
  function actStockQty(r) { var t = actTier(r); return t ? t.stockQty : r.oemStockQty; }
  // Confirmed lead time for the active tier ("3 weeks", "factory stock"),
  // null when unset. Only the OEM row carries it (twins price/stock differ);
  // the confirmed value is a factory ETA that applies regardless of tier.
  function actLeadConfirmed(r) { return r.leadConfirmed || null; }
  // Availability badge for a row. Real on-hand stock ALWAYS wins ("In stock"),
  // and green is reserved for actual on-hand items. Every factory-order row
  // shows the same amber "Factory" badge — a confirmed "factory stock" value
  // displays no differently — UNLESS the confirmed lead time is a real ETA
  // ("3 weeks"), which replaces the badge text ("3 wk"). The STALE pulled
  // lead-time days are never shown; only confirmed ETAs.
  function leadInfo(r) {
    if (!r.found) return { col: "Not found", word: "Not found", cls: "na" };
    var v = actVariant(r);
    // "In stock" is a promise about THIS quantity. Until 2026-10-05 a row read
    // "In stock" whenever any unit existed: 16 breaker plate liners against 1 on
    // the shelf. Short stock now shows the real count in amber, rest factory order.
    if (v === "in-stock") {
      var have = actStockQty(r);
      if (have != null && have > 0 && r.qty > have) {
        var rest = r.qty - have;
        return { col: have + " in stock", word: have + " in stock, " + rest + " factory order", cls: "factory",
                 tip: have + " in stock now, the other " + rest + " ship as a factory order. Shipping time may vary, please contact support." };
      }
    }
    if (v === "factory") {
      var c = actLeadConfirmed(r);
      if (c && c.toLowerCase().indexOf("stock") === -1) {
        // "3 weeks" -> "3 wk" for the tight column; full phrase in exports.
        var short = c.replace(/\s*weeks?/i, " wk").replace(/\s*days?/i, " d").trim();
        return { col: short, word: "Factory order, about " + c, cls: "factory",
                 tip: "Factory order, about " + c + ". Confirmed lead time." };
      }
    }
    return lead(v);
  }
  // Human/export suffix + SKU suffix for the selected spec.
  function specNote(r) { return r.spec === "rs" ? " (Reliant)" : r.spec === "am" ? " (aftermarket)" : ""; }
  function specSkuSuffix(r) { return r.spec === "rs" ? "-RS" : r.spec === "am" ? "-AM" : ""; }

  function init(root) {
    if (root.dataset.hqtReady === "true") return;
    root.dataset.hqtReady = "true";

    var endpoint = root.getAttribute("data-endpoint");
    var email = root.getAttribute("data-email") || "";
    // "quote" (homepage order pad) or "container" (Container Direct landing
    // page): same parser, lookup and share link; container mode shows
    // pounds instead of prices and a fill gauge instead of totals.
    var mode = root.getAttribute("data-hqt-mode") || "quote";
    var capLb = parseFloat(root.getAttribute("data-cap-lb")) || 38000;
    // A catalog weight under this is as likely a placeholder as a measurement,
    // and a line this light barely moves a container: weighed at quote.
    var GAUGE_MIN_LB = 25;
    var freightBase = parseFloat(root.getAttribute("data-freight-base")) || 75;
    var freightPerLb = parseFloat(root.getAttribute("data-freight-per-lb")) || 0.6;
    var freightPallet = parseFloat(root.getAttribute("data-freight-pallet")) || 300;
    var freightMin = parseFloat(root.getAttribute("data-freight-min")) || 45;

    var els = {
      input: root.querySelector("[data-hqt-input]"),
      textarea: root.querySelector("[data-hqt-textarea]"),
      priceBtn: root.querySelector("[data-hqt-price]"),
      results: root.querySelector("[data-hqt-results]"),
      tabList: root.querySelector("[data-hqt-tab-list]"),
      tabQuote: root.querySelector("[data-hqt-tab-quote]"),
      quoteCount: root.querySelector("[data-hqt-quote-count]"),
      ccyCode: root.querySelector("[data-hqt-ccy]"),
      tbody: root.querySelector("[data-hqt-tbody]"),
      notes: root.querySelector("[data-hqt-notes]"),
      msgmode: root.querySelector("[data-hqt-msgmode]"),
      manualCta: root.querySelector("[data-hqt-manual]"),
      scroll: root.querySelector(".hqt__scroll"),
      totalsBox: root.querySelector(".hqt__totals"),
      subtotal: root.querySelector("[data-hqt-subtotal]"),
      shipLabel: root.querySelector("[data-hqt-ship-label]"),
      freight: root.querySelector("[data-hqt-freight]"),
      inboundRow: root.querySelector("[data-hqt-inbound-row]"),
      inboundLabel: root.querySelector("[data-hqt-inbound-label]"),
      inbound: root.querySelector("[data-hqt-inbound]"),
      savingsNote: root.querySelector("[data-hqt-savings-note]"),
      savings: root.querySelector("[data-hqt-savings]"),
      total: root.querySelector("[data-hqt-total]"),
      quote: root.querySelector("[data-hqt-quote]"),
      copy: root.querySelector("[data-hqt-copy]"),
      share: root.querySelector("[data-hqt-share]"),
      addAll: root.querySelector("[data-hqt-addall]"),
      dest: root.querySelector("[data-hqt-dest]"),
      file: root.querySelector("[data-hqt-file]"),
      tile: root.querySelector("[data-hqt-tile]"),
      tileTitle: root.querySelector("[data-hqt-tile-title]"),
      tileSub: root.querySelector("[data-hqt-tile-sub]"),
      scanbar: root.querySelector("[data-hqt-scanbar]"),
      country: root.querySelector("[data-hqt-country]"),
      province: root.querySelector("[data-hqt-province]"),
      zip: root.querySelector("[data-hqt-zip]"),
      ratesBtn: root.querySelector("[data-hqt-rates]"),
      rateNote: root.querySelector("[data-hqt-rate-note]"),
      gauge: root.querySelector("[data-hqt-gauge]"),
      gaugeLoad: root.querySelector("[data-hqt-gauge-load]"),
      gaugeCap: root.querySelector("[data-hqt-gauge-cap]"),
      gaugePct: root.querySelector("[data-hqt-gauge-pct]"),
      gaugeBar: root.querySelector("[data-hqt-gauge-bar]"),
      gaugeLeft: root.querySelector("[data-hqt-gauge-left]"),
      gaugeLeftLabel: root.querySelector("[data-hqt-gauge-left-label]"),
      gaugeFits: root.querySelector("[data-hqt-gauge-fits]"),
      gaugeLegend: root.querySelector("[data-hqt-gauge-legend]"),
      gaugeMsg: root.querySelector("[data-hqt-gauge-msg]"),
      gaugeNote: root.querySelector("[data-hqt-gauge-note]"),
    };
    if (!endpoint || !els.textarea || !els.tbody) return;

    // liveRate: a real carrier rate for the CURRENT list + destination, from
    // the same app endpoint as the product page's "Shipping & customs" block.
    // `sig` snapshots the list (parts/specs/qtys); any edit invalidates it and
    // the totals fall back to the heuristic estimate until recalculated.
    var state = { rows: [], unit: "lb", notes: [], liveRate: null };
    var priceLabel = els.priceBtn.textContent; // "Price it →", restored after pricing

    // ---- Pad parsing ----
    // The pad is officially "one part number per line, optional qty" — but real
    // buyers also type prose ("Looking for 17 of a 10100 spring. 505-916-6743")
    // or use it like a contact form (a name, a phone number). Instead of sending
    // those lines to the SKU lookup verbatim (guaranteed "Not found"), pull
    // phones/emails out, mine prose lines for part-number-shaped tokens (every
    // SKU in the catalog contains a digit) plus a quantity, and keep the buyer's
    // words as notes that ride along with the RFQ email / copy export.
    var EMAIL_RE = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/;
    // Requires a separator so plain 10-digit part numbers never read as phones.
    // \s* inside the parens: "(520 ) 761-1984" must strip WHOLE — a half-strip
    // leaves "761-1984" behind, which is exactly SKU-shaped (815-1051 is a real
    // BTI part), and the freed "520" area code then reads as a quantity.
    var PHONE_RE = /(?:\+?1[\s.\-]?)?\(?\s*\d{3}\s*\)?[\s.\-]\d{3}[\s.\-]?\d{4}(?!\d)/;
    // A line that announces contact info ("TEL: ... FAX ...") never carries a
    // part number; the whole line rides along as a note instead. Deliberately
    // NOT matching mobile/movil — "mobile crusher" is product vocabulary here.
    var CONTACT_LINE_RE = /\b(tel[eé]?fono|tel|phone|fax|cel|celular|whatsapp)\b/i;
    // "CP85621" (MX Código Postal) is SKU-shaped but never a part. A BARE
    // 5-digit token is only dropped when the line says it's an address — 10100
    // is a real spring SKU, so bare digits can't be blocked outright.
    var CP_TOKEN_RE = /^c\.?p\.?\d{4,6}$/i;
    var ADDRESSY_RE = /\b(usa|u\.s\.a\.|ee\.?\s?uu\.?|zip|c\.?p\.?|p\.?o\.?\s?box)\b/i;

    function skuLike(tok) {
      return tok.length >= 4 && /^[A-Za-z0-9][A-Za-z0-9._\/-]*$/.test(tok) && /\d/.test(tok);
    }

    // Parse the textarea into { items: [{ query, qty }], notes: [raw lines] },
    // summing duplicate part numbers.
    function parseLines() {
      var lines = String(els.textarea.value || "").split(/\r?\n/);
      var order = [];
      var byKey = {};
      var notes = [];
      // fromNote marks items whose ONLY source is a prose line — Edit-list must
      // not re-emit those as part lines (the prose note re-extracts them on the
      // next Price-it, so emitting both would double the quantity).
      function add(sku, qty, fromNote) {
        var key = sku.toUpperCase();
        if (byKey[key]) {
          // A prose mention and an explicit part line for the SAME number are
          // one request, not two: procurement pastes describe the part in a
          // labeled sentence AND repeat it as "815-1590 EA 1". Summing both
          // double-counted the quantity, so the explicit line's qty REPLACES a
          // prose-sourced one (and a later prose mention of an already-explicit
          // item adds nothing). Two explicit lines still sum - a real repeat.
          if (!fromNote && byKey[key].fromNote) {
            byKey[key].qty = Math.max(1, qty);
            byKey[key].fromNote = false;
          } else if (fromNote && !byKey[key].fromNote) {
            /* explicit already recorded; prose repeat is just description */
          } else {
            byKey[key].qty += qty;
          }
        } else {
          byKey[key] = { query: sku, qty: Math.max(1, qty), fromNote: !!fromNote };
          order.push(key);
        }
      }
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i].trim();
        if (!line) continue;

        // Contact lines carry no parts. This is the line that minted
        // "761-1984 x520" out of "TEL: (520 ) 761-1984 FAX (520) 761-4547".
        if (CONTACT_LINE_RE.test(line)) {
          notes.push(line);
          continue;
        }

        var hasContact = EMAIL_RE.test(line) || PHONE_RE.test(line);
        var stripped = line
          .replace(new RegExp(EMAIL_RE.source, "g"), " ")
          .replace(new RegExp(PHONE_RE.source, "g"), " ");
        var toks = stripped
          .split(/[\s,;:|]+/)
          .map(function (t) { return t.replace(/^[("'\[]+/, "").replace(/[-)"'\].,;:!?]+$/, ""); })
          .filter(Boolean);
        var addressy = ADDRESSY_RE.test(line);
        toks = toks.filter(function (t) {
          if (CP_TOKEN_RE.test(t)) return false;             // CP85621
          if (addressy && /^\d{5}$/.test(t)) return false;  // bare ZIP on an address line
          return true;
        });
        // Unit words are quantity vocabulary, not description: "815-1590 EA 1"
        // is an explicit part line, and letting EA flip it to prose made the
        // labeled description above it double the quantity (2026-08-31).
        var UNIT_WORD_RE = /^(?:ea|each|pcs?|pieces?|piezas?|pzas?|units?|unidades)$/i;
        var prose = toks.some(function (t) { return t.length >= 2 && /^[A-Za-z][A-Za-z'&]*$/.test(t) && !UNIT_WORD_RE.test(t); });

        if (!hasContact && !prose) {
          // Normal part line. Pull the SKU-shaped token(s) plus an optional
          // small-integer quantity by SHAPE, not position: a part number is >=4
          // chars (skuLike); a quantity is 1-3 bare digits (or "x2"). They never
          // collide, so this tolerates leading list/qty markers ("1- 941771",
          // "2) N01563036", "15- 949640528700") and any order ("941771 2",
          // "50 941771"). The old "last number = qty" rule mis-read an all-digit
          // PART NUMBER as the quantity and the "1-"/"2-" marker as the SKU.
          var pSkus = toks.filter(skuLike);
          if (pSkus.length) {
            var pQtyTok = toks.filter(function (t) {
              return /^x?\d{1,3}$/i.test(t) && parseInt(t.replace(/^x/i, ""), 10) > 0;
            })[0];
            var pQty = pSkus.length === 1 && pQtyTok ? parseInt(pQtyTok.replace(/^x/i, ""), 10) : 1;
            pSkus.forEach(function (s) { add(s, pQty); });
          } else {
            // No SKU-shaped token — keep the raw line as one query so it still
            // reaches us instead of being silently dropped.
            add(line, 1);
          }
          continue;
        }

        // Prose / contact line: extract part-number-shaped tokens plus a qty
        // ("17 of a 10100" → 10100 x17) and keep the buyer's words verbatim as
        // an RFQ note (names, phone numbers, "spring", …).
        // On a prose line a bare number is more often a dimension (a 5″
        // idler once read as qty 5) or an address fragment than a count. Only
        // accept a quantity with an explicit marker: "x2", "qty 3", "17 of",
        // "7 piezas/pcs/units". "5 x 10" (a dimension pair) stays qty 1.
        // Labels beat shape (2026-08-31). Procurement pastes are often
        // SELF-LABELING - "PART NUMBER: 815-0629, MODEL: TM25X/TB980X, SERIAL
        // NUMBER: 2007080" - and shape-extraction minted RFQ line items for
        // the MODEL and the SERIAL. When the buyer says which token is the
        // part, believe them: PART NUMBER / P/N captures become the items,
        // and MODEL / SERIAL captures are context that never becomes a line
        // item (both stay in the verbatim note). Lines without labels keep
        // the existing shape-based behavior unchanged.
        var partLabeled = [], ctx = {}, m2;
        var PART_LABEL_RE = /\b(?:part\s*(?:number|no|#)?|p\/n|pn)\s*[:#.]?\s*([A-Za-z0-9][A-Za-z0-9._\/-]{3,})/gi;
        var CTX_LABEL_RE = /\b(?:model|serial\s*(?:number|no|#)?|s\/n|sn)\s*[:#.]?\s*([A-Za-z0-9][A-Za-z0-9._\/-]{2,})/gi;
        while ((m2 = CTX_LABEL_RE.exec(line))) ctx[m2[1].toUpperCase()] = true;
        while ((m2 = PART_LABEL_RE.exec(line))) {
          if (!ctx[m2[1].toUpperCase()] && skuLike(m2[1])) partLabeled.push(m2[1]);
        }
        var seenSku = {};
        var skus = (partLabeled.length
          ? partLabeled
          : toks.filter(function (t) { return skuLike(t) && !ctx[t.toUpperCase()]; })
        ).filter(function (t) {
          var k = t.toUpperCase();
          if (seenSku[k]) return false;
          seenSku[k] = true;
          return true;
        });
        var qm = line.match(/\b(?:x|qty[.:]?\s*)(\d{1,3})\b/i) ||
          line.match(/\b(\d{1,3})\s*(?:x\b(?!\s*\d)|of\b|pcs?\b|pieces?\b|piezas?\b|pzas?\b|units?\b|unidades\b|ea\b|each\b)/i);
        var lineQty = skus.length === 1 && qm ? (parseInt(qm[1], 10) || 1) : 1;
        skus.forEach(function (s) { add(s, lineQty, true); });
        notes.push(line);
      }
      return { items: order.map(function (k) { return byKey[k]; }), notes: notes };
    }

    function parsedCount() {
      var p = parseLines();
      return p.items.length + p.notes.length;
    }
    // Prose lines pulled out of the pad ride along as notes (the copy export
    // and the Get a quote dialog's Notes field). Contact details are the
    // dialog's job, so the pad itself stays part numbers only.
    function rfqNotes() {
      return (state.notes || []).slice();
    }
    // List / Quote tabs: LIST = input view, QUOTE = results view. setView keeps
    // the tab active state in sync; updateQuoteTab enables the Quote tab + its
    // count once there's something priced (or a notes-only RFQ).
    function setView(showResults) {
      els.input.hidden = showResults;
      els.results.hidden = !showResults;
      if (els.tabList) {
        els.tabList.classList.toggle("is-active", !showResults);
        els.tabList.setAttribute("aria-pressed", String(!showResults));
      }
      if (els.tabQuote) {
        els.tabQuote.classList.toggle("is-active", showResults);
        els.tabQuote.setAttribute("aria-pressed", String(showResults));
      }
    }
    function updateQuoteTab() {
      if (!els.tabQuote) return;
      var n = state.rows.length;
      els.tabQuote.disabled = n === 0 && !(state.notes && state.notes.length);
      if (els.quoteCount) els.quoteCount.textContent = n > 0 ? " · " + n : "";
    }
    function setPriceDisabled() {
      var p = parseLines();
      var prose = p.notes.length > 0;
      els.priceBtn.disabled = !p.items.length && !prose;
      // With nothing SKU-shaped anywhere, "pricing" is not what happens next;
      // label the button for the message it is about to send.
      els.priceBtn.textContent = !p.items.length && prose ? "Send as quote request \u2192" : priceLabel;
    }

    // ---- Lookup ----
    function priceIt() {
      var parsed = parseLines();
      var items = parsed.items;
      state.notes = parsed.notes;
      if (!items.length) {
        if (!state.notes.length) return;
        // Notes-only submission (a name / phone / message, no part numbers):
        // no lookup to run — show the results panel with the notes strip and
        // the Email-an-RFQ action.
        state.rows = [];
        resetAddAll();
        render();
        setView(true);
        return;
      }
      els.priceBtn.disabled = true;
      els.priceBtn.textContent = "Pricing…";

      // Query each pasted SKU plus its "-RS" (Reliant) and "-AM" (aftermarket)
      // twins in one request.
      var skus = [];
      items.forEach(function (it) {
        skus.push(it.query);
        skus.push(it.query + "-RS");
        skus.push(it.query + "-AM");
      });

      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ skus: skus }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.json();
        })
        .then(function (data) {
          var byKey = {};
          ((data && data.results) || []).forEach(function (res) {
            byKey[String(res.query || "").toUpperCase()] = res;
          });
          // A twin tier is offerable only when it exists AND has a real price
          // (a $0 twin is quote-only and would just confuse the toggle).
          function tier(oem, res) {
            if (!oem.found || !res || !res.found) return null;
            if (res.priceAmount == null || Number(res.priceAmount) <= 0) return null;
            return {
              price: Number(res.priceAmount),
              weightKg: res.weightKg != null ? Number(res.weightKg) : 0,
              variant: res.stockVariant || "unknown",
              stockQty: res.stockQty != null ? Number(res.stockQty) : null,
              leadDays: res.leadTimeDays != null ? Number(res.leadTimeDays) : null,
              url: res.url || null,
              variantId: res.variantId || null,
            };
          }
          state.rows = items.map(function (it) {
            var oem = byKey[it.query.toUpperCase()] || { found: false };
            var rs = tier(oem, byKey[(it.query + "-RS").toUpperCase()]);
            var am = tier(oem, byKey[(it.query + "-AM").toUpperCase()]);
            return {
              query: it.query,
              qty: it.qty,
              fromNote: !!it.fromNote,
              found: !!oem.found,
              part: (oem.sku || it.query).toUpperCase(),
              desc: oem.description || "not in catalog",
              oemPrice: oem.priceAmount != null ? Number(oem.priceAmount) : 0,
              oemWeightKg: oem.weightKg != null ? Number(oem.weightKg) : 0,
              oemVariant: oem.found ? oem.stockVariant || "unknown" : "na",
              oemStockQty: oem.found && oem.stockQty != null ? Number(oem.stockQty) : null,
              inboundShip: oem.inboundShip === true,
              oemLeadDays: oem.found && oem.leadTimeDays != null ? Number(oem.leadTimeDays) : null,
              leadConfirmed: (oem.found && oem.leadTimeConfirmed) || null,
              url: oem.url || null,
              oemVariantId: oem.variantId || null,
              rs: rs,
              am: am,
              spec: "oem",
            };
          });
          // A number mined out of a SENTENCE ("need these for an HP400 by
          // Friday") is a machine model or a dimension as often as a part. It
          // earns a row only when the catalog actually has it; otherwise the
          // sentence just rides along as a note, which still reaches us. A
          // number on its own line is a deliberate request and stays, found
          // or not.
          state.rows = state.rows.filter(function (r) { return r.found || !r.fromNote; });
          resetAddAll();
          render();
          setView(true);
          els.priceBtn.textContent = priceLabel;
        })
        .catch(function () {
          els.priceBtn.textContent = "Try again →";
          els.priceBtn.disabled = false;
        });
    }

    // ---- Totals ----
    function weightLbTotal() {
      return state.rows.reduce(function (s, r) {
        return s + (r.found ? actWeight(r) * KG_TO_LB * r.qty : 0);
      }, 0);
    }

    // Signature of the quotable list (parts, specs, quantities): a live
    // carrier rate is only valid while this stays unchanged.
    function itemsSig() {
      return state.rows
        .filter(function (r) { return r.found; })
        .map(function (r) { return r.part + specSkuSuffix(r) + ":" + r.qty; })
        .join(",");
    }

    function liveRateValid() {
      return !!(state.liveRate && state.liveRate.sig === itemsSig());
    }

    // Parcel or pallet estimate for a weight, in USD. Shared by the leg to the
    // buyer and, for special-order rows, the leg from the manufacturer.
    function shipEstimate(wlb) {
      if (wlb <= PARCEL_MAX_LB) {
        return { amount: Math.max(PARCEL_MIN, Math.round(PARCEL_BASE + wlb * PARCEL_PER_LB)), liftgate: false };
      }
      /* Freight is PER-PALLET at the bottom and economy-of-scale by weight
         above it, so the model is a pallet floor plus declining marginal
         bands. Calibrated 2026-09-01 on two real quotes:
           - operator: one pallet (to ~2,000 lb) ships ~$300 anywhere in
             the USA - so 800 lb estimating $555 was ~2x real;
           - two C150 XT710 jaw dies to SD 57754: 6,505 lb piece $2,400,
             8,499 lb piece $3,000 (real $/lb ~0.36 at scale).
         Bands are fractions of the per-lb setting (0.60 default): zero to
         2,000 lb (the pallet flat covers it), 0.7875x to 10,000 lb, 0.6x
         above. Lands: 800 lb -> $300, 6,505 -> $2,430 (+1%),
         8,499 -> $3,370 (+12%), 15,004 combined -> $5,880 (+9% over the
         $5,400 two-shipment sum). Tuning freight_per_lb scales the curve;
         freight_pallet moves the floor. */
      var bands = [
        [2000, 0],
        [10000, 0.7875],
        [Infinity, 0.6]
      ];
      var cost = freightPallet, prev = 0;
      for (var bi = 0; bi < bands.length && prev < wlb; bi++) {
        var span = Math.min(wlb, bands[bi][0]) - prev;
        cost += span * freightPerLb * bands[bi][1];
        prev += span;
      }
      return { amount: Math.max(freightMin, Math.round(cost / 5) * 5), liftgate: true };
    }

    function totals() {
      var found = state.rows.filter(function (r) { return r.found; });
      var priced = found.filter(actPriced);
      var subtotal = priced.reduce(function (s, r) { return s + actPrice(r) * r.qty; }, 0);
      var wlb = weightLbTotal();
      // No weight data at all -> any $ figure is fiction; quote shipping with
      // the order instead of estimating from 0 lb.
      var noWeight = wlb <= 0;
      // Special-order rows (ship-inbound tag) travel twice: manufacturer to
      // us, then to the buyer. Checkout charges both legs (their delivery
      // profile doubles the carrier rate), so the estimate shows the second
      // leg as its own line, priced like the first on those rows' weight. A
      // weightless special-order row still rates at the parcel minimum.
      var inboundRows = found.filter(function (r) { return r.inboundShip === true; });
      var inbound = inboundRows.length > 0;
      var inboundLb = inboundRows.reduce(function (s, r) { return s + actWeight(r) * KG_TO_LB * r.qty; }, 0);
      var shipping = 0, inboundShipping = 0;
      var liftgate = false, inboundLiftgate = false;
      if (found.length && !noWeight) {
        var est = shipEstimate(wlb);
        shipping = est.amount;
        liftgate = est.liftgate;
        if (inbound) {
          var est2 = shipEstimate(Math.max(inboundLb, 0.01));
          inboundShipping = est2.amount;
          inboundLiftgate = est2.liftgate;
        }
      }
      // A live carrier rate for this exact list beats the heuristic. It comes
      // from draftOrderCalculate, so it already carries both legs for the
      // special-order rows: one line, no separate second leg.
      var live = liveRateValid() ? state.liveRate : null;
      if (live) {
        shipping = live.amount;
        inboundShipping = 0;
        liftgate = false;
        noWeight = false;
      }
      var savings = found.reduce(function (s, r) {
        var t = actTier(r);
        return s + (t ? Math.max(0, r.oemPrice - t.price) * r.qty : 0);
      }, 0);
      return { subtotal: subtotal, shipping: shipping, inboundShipping: inboundShipping, liftgate: liftgate,
        inboundLiftgate: inboundLiftgate, inbound: inbound, noWeight: noWeight, live: live, savings: savings,
        total: subtotal + shipping + inboundShipping, pricedCount: priced.length, rfqCount: found.length - priced.length };
    }

    function fmtLb(n) { return Math.round(n).toLocaleString("en-US"); }

    function fmtWeight(lb) {
      var kg = state.unit === "kg";
      var v = kg ? lb / KG_TO_LB : lb;
      var s = v < 10 ? v.toFixed(1) : Math.round(v).toLocaleString("en-US");
      return s + (kg ? " kg" : " lb");
    }

    // ---- Render ----
    // One dense row per part: # / part / description / availability / qty /
    // unit / amount. CSS lays it out as a single-line table row when the pad is
    // wide, or a compact two-line row when narrow.
    function pillHtml(ld) {
      return '<span class="hqt__pill hqt__pill--' + ld.cls + '"' + (ld.tip ? ' title="' + esc(ld.tip) + '"' : "") + ">" + esc(ld.col) + "</span>";
    }

    function rowHtml(r, i) {
      var priced = actPriced(r);
      var price = actPrice(r);
      var ld = leadInfo(r);

      // Link the part number to its product page (opens in a new tab so the
      // buyer keeps their quote). Falls back to plain text when there's no URL.
      var url = r.found ? actUrl(r) : null;
      var partHtml = url
        ? '<a class="hqt__part-link" href="' + esc(url) + '" target="_blank" rel="noopener">' + esc(r.part) + "</a>"
        : esc(r.part);

      // Container mode: pounds, not dollars. A line with no trusted weight
      // shows "on quote" and stays out of the gauge.
      var wLb = r.found ? actWeight(r) * KG_TO_LB : 0;
      var weighed = wLb >= GAUGE_MIN_LB;
      var unitCell = mode === "container"
        ? (weighed ? fmtLb(wLb) + " lb" : '<span class="hqt__rfq">on quote</span>')
        : (priced ? usd(price) + " ea" : "-");
      var amtCell = mode === "container"
        ? (weighed ? fmtLb(wLb * r.qty) + " lb" : '<span class="hqt__rfq">at quote</span>')
        : (priced ? usd(price * r.qty) : '<span class="hqt__rfq">RFQ</span>');
      var cells =
        '<div class="hqt__cell hqt__cell--ln">' + String(i + 1).padStart(2, "0") + "</div>" +
        '<div class="hqt__cell hqt__cell--part">' + partHtml + "</div>" +
        '<div class="hqt__cell hqt__cell--desc">' + esc(r.desc) + "</div>" +
        '<div class="hqt__cell hqt__cell--avail">' + pillHtml(ld) + "</div>" +
        '<div class="hqt__cell hqt__cell--qty"><input class="hqt__qty" type="text" inputmode="numeric" value="' +
        esc(r.qty) + '" data-hqt-qty="' + i + '" aria-label="Quantity"></div>' +
        '<div class="hqt__cell hqt__cell--unit">' + unitCell + "</div>" +
        '<div class="hqt__cell hqt__cell--amt">' + amtCell + "</div>";

      // Alternate tiers are offered only when a twin variant exists: -RS
      // (genuine Metso Reliant) and/or -AM (aftermarket). The segmented control
      // makes the spec choice explicit; the row's unit/amount/availability
      // update on switch, and each cheaper tier shows its saving.
      var alt = "";
      if (r.rs || r.am) {
        var opt = function (set, name, active, tierPrice) {
          var saving = tierPrice != null ? Math.max(0, (r.oemPrice - tierPrice) * r.qty) : 0;
          return (
            '<button type="button" class="hqt__seg-opt' + (active ? " is-active" : "") +
            '" data-hqt-alt-set="' + set + '" data-hqt-row="' + i + '" aria-pressed="' + active + '">' +
            name + (saving > 0 ? ' <span class="hqt__seg-save">save ' + usd0(saving) + "</span>" : "") + "</button>"
          );
        };
        alt =
          '<div class="hqt__cell hqt__cell--alt">' +
          '<span class="hqt__seg-label">Spec</span>' +
          '<span class="hqt__seg" role="group" aria-label="Choose OEM, Reliant or aftermarket">' +
          opt("oem", "OEM", r.spec === "oem", null) +
          (r.rs ? opt("rs", "Reliant", r.spec === "rs", r.rs.price) : "") +
          (r.am ? opt("am", "Aftermarket", r.spec === "am", r.am.price) : "") +
          "</span></div>";
      }

      return '<div class="hqt__row' + (r.found ? "" : " hqt__row--missing") + '">' + cells + alt + "</div>";
    }

    function renderTotals() {
      var t = totals();
      var hasPriced = t.pricedCount > 0;
      els.subtotal.textContent = hasPriced ? usd(t.subtotal) : "RFQ";
      els.freight.textContent = t.live ? usd(t.shipping) : t.noWeight ? "with quote" : usd0(t.shipping);
      // Special-order rows add a second leg (manufacturer to us). With the
      // heuristic it gets its own line and the first leg reads "to you"; a
      // live rate already carries both legs, so it stays one line.
      var secondLeg = t.inbound && !t.live && !t.noWeight && t.inboundShipping > 0;
      if (els.shipLabel) {
        var toYou = secondLeg ? " to you" : "";
        els.shipLabel.innerHTML = t.live
          ? esc(t.live.name)
          : t.noWeight
            ? "Shipping"
            : t.liftgate
              ? "Freight LTL" + toYou + ' <span class="hqt__free">&middot; lift-gate free</span>'
              : "Ground" + toYou + " (est.)";
      }
      if (els.inboundRow) {
        els.inboundRow.hidden = !secondLeg;
        if (els.inboundLabel) els.inboundLabel.textContent = (t.inboundLiftgate ? "Freight" : "Ground") + " from the manufacturer (est.)";
        if (els.inbound) els.inbound.textContent = usd0(t.inboundShipping);
      }
      els.total.textContent = hasPriced ? usd(t.total) : "RFQ";
      if (els.savingsNote) {
        // visibility (not hidden) so the caption keeps its line and nothing
        // below it jumps when savings appear/disappear.
        els.savingsNote.hidden = false;
        els.savingsNote.style.visibility = t.savings > 0.5 ? "visible" : "hidden";
        if (els.savings) els.savings.textContent = usd0(t.savings);
      }
      if (els.ccyCode) els.ccyCode.textContent = currencyCfg().code;
      // Rate note: the element always keeps its line (no layout jumps) — only
      // the text comes and goes.
      if (els.rateNote) {
        if (t.live) {
          els.rateNote.textContent = t.live.note;
        } else if (!els.rateNote.dataset.msg) {
          els.rateNote.textContent = "";
        }
      }
      // Shared ?dest= link: quote the live rate as soon as the rows are in.
      if (state.pendingAutoRates && els.dest && state.rows.some(function (r) { return r.found; })) {
        state.pendingAutoRates = false;
        els.dest.dispatchEvent(new Event("submit", { cancelable: true }));
      }
    }

    // Container mode: the load is a weight budget. Only trusted weights
    // (GAUGE_MIN_LB and up) count; the rest are weighed at quote. Two numbers
    // (loaded, room left), then a bar with one band per weighed line in table
    // order and a legend under it (design pass 2026-10-26).
    var GAUGE_SHADES = ["#0b4971", "#3c7aa6", "#7faccb", "#245b84", "#5e93bd"];
    function renderGauge() {
      if (!els.gauge) return;
      var load = 0, unweighed = 0, segs = [];
      state.rows.forEach(function (r) {
        if (!r.found) { unweighed += 1; return; }
        var w = actWeight(r) * KG_TO_LB;
        if (w >= GAUGE_MIN_LB) {
          load += w * r.qty;
          segs.push({ r: r, w: w, lb: w * r.qty });
        } else {
          unweighed += 1;
        }
      });
      var pct = capLb > 0 ? (load / capLb) * 100 : 0;
      var over = load > capLb;
      var room = capLb - load;
      state.gauge = { load: load, unweighed: unweighed, lines: state.rows.length };
      els.gaugeLoad.textContent = fmtLb(load);
      els.gaugeCap.textContent = fmtLb(capLb);
      els.gaugePct.textContent = String(Math.round(pct));
      els.gauge.classList.toggle("is-over", over);

      // Right-hand number: the room left, or how far over.
      els.gaugeLeftLabel.textContent = over ? "Over by" : "Room left";
      els.gaugeLeft.textContent = fmtLb(Math.abs(room));
      var fits;
      if (over) {
        fits = (load / capLb).toFixed(1) + " containers";
      } else if (segs.length) {
        // The heaviest line that still fits at least once is the yardstick.
        var pick = null;
        segs.slice().sort(function (a, b) { return b.w - a.w; }).some(function (s) {
          if (Math.floor(room / s.w) >= 1) { pick = s; return true; }
          return false;
        });
        fits = pick ? "Fits about " + Math.floor(room / pick.w) + " more " + pick.r.part : "Nothing else on this list fits";
      } else {
        fits = "Weighed at quote";
      }
      els.gaugeFits.textContent = fits;

      // The bar and its legend. Over capacity it is one red bar and the
      // legend gives way to the split-or-trim line.
      var bar = "", legend = "";
      if (over) {
        bar = '<span class="hqt__gauge-seg hqt__gauge-seg--over" style="width:100%"></span>';
      } else {
        segs.forEach(function (s, i) {
          var shade = GAUGE_SHADES[i % GAUGE_SHADES.length];
          bar += '<span class="hqt__gauge-seg" style="width:' + ((s.lb / capLb) * 100).toFixed(2) + '%;background:' + shade + '"></span>';
          legend += '<span class="hqt__gauge-key"><span class="hqt__gauge-swatch" style="background:' + shade + '"></span>' + esc(s.r.part) + " · " + fmtLb(s.lb) + " lb</span>";
        });
        if (segs.length) legend += '<span class="hqt__gauge-key"><span class="hqt__gauge-swatch hqt__gauge-swatch--room"></span>Room · ' + fmtLb(room) + " lb</span>";
      }
      els.gaugeBar.innerHTML = bar;
      els.gaugeBar.setAttribute("aria-label", over ? "Container over capacity" : "Container " + Math.round(pct) + " percent full, by line");
      els.gaugeLegend.innerHTML = legend;
      els.gaugeLegend.hidden = !legend;
      els.gaugeMsg.hidden = !over;
      els.gaugeMsg.textContent = over
        ? "This is " + (load / capLb).toFixed(1) + " containers. Split it into " + Math.ceil(load / capLb) + ", or trim " + fmtLb(load - capLb) + " lb to fit one."
        : "";
      els.gaugeNote.hidden = !unweighed;
      els.gaugeNote.textContent = unweighed + (unweighed === 1 ? " line is" : " lines are") +
        " weighed at quote (no weight on file, or under " + GAUGE_MIN_LB + " lb).";
    }

    // The load as one line for our side of the request (the endpoint labels
    // it "Container load"); never typed into the buyer's notes.
    function gaugeSummary() {
      var g = state.gauge;
      if (!g) return "";
      return fmtLb(g.load) + " lb of " + fmtLb(capLb) + " lb, " + g.lines + " line" +
        (g.lines === 1 ? "" : "s") + (g.unweighed ? ", " + g.unweighed + " weighed at quote" : "");
    }

    function renderNotes() {
      if (!els.notes) return;
      var n = rfqNotes();
      els.notes.hidden = !n.length;
      els.notes.innerHTML = n.length
        ? '<span class="hqt__notes-label">Noted for your RFQ</span>' +
          n.map(function (t) { return '<span class="hqt__notes-item">' + esc(t) + "</span>"; }).join("")
        : "";
    }

    // The parts-book ask. Only shown when the lookup came back with numbers we
    // could not match, which is the one moment it answers a question the buyer
    // actually has. Naming the count keeps it specific instead of a slogan.
    function renderManualCta() {
      if (!els.manualCta) return;
      var misses = 0;
      state.rows.forEach(function (r) {
        if (!r.found) misses += 1;
      });
      if (!misses) {
        els.manualCta.hidden = true;
        els.manualCta.innerHTML = "";
        return;
      }
      els.manualCta.innerHTML =
        "We could not match " +
        misses +
        (misses === 1 ? " number" : " of those numbers") +
        ". " +
        '<a href="/apps/machines/parts-manual?source=quote-tool">Send us the parts book</a>' +
        " and we will send it back with every part number linked to price, stock or a quick quote.";
      els.manualCta.hidden = false;
    }

    function render() {
      els.tbody.innerHTML = state.rows.map(rowHtml).join("");
      // Notes-only submissions have no table or money to show — just the notes
      // strip and the actions.
      if (els.scroll) els.scroll.hidden = !state.rows.length;
      if (els.totalsBox) els.totalsBox.hidden = !state.rows.length;
      if (els.gauge) els.gauge.hidden = !state.rows.length;
      // Message mode: no rows means this is a message, not a failed parts
      // list. Swap the table furniture for the explainer and make Email the
      // one primary action.
      var msgOnly = !state.rows.length;
      if (els.msgmode) els.msgmode.hidden = !msgOnly;
      if (els.copy) els.copy.hidden = msgOnly;
      if (els.share) els.share.hidden = msgOnly;
      if (els.addAll) {
        els.addAll.hidden = msgOnly || !state.rows.some(function (r) { return r.found && actUrl(r); });
      }
      if (els.quote) {
        els.quote.textContent = msgOnly ? "Send as quote request \u2192" : els.quote.dataset.label || "Get a quote";
      }
      renderNotes();
      renderManualCta();
      if (mode === "container") renderGauge(); else renderTotals();
      updateQuoteTab();
    }

    // ---- Row edits ----
    // Typing in the qty field: update this card's amount in place (keeps focus),
    // then the totals.
    els.tbody.addEventListener("input", function (e) {
      var t = e.target;
      if (!t.hasAttribute || !t.hasAttribute("data-hqt-qty")) return;
      var i = parseInt(t.getAttribute("data-hqt-qty"), 10);
      state.rows[i].qty = Math.max(1, parseInt(String(t.value).replace(/[^0-9]/g, ""), 10) || 1);
      var row = state.rows[i];
      var rowEl = t.closest(".hqt__row");
      var amt = rowEl && rowEl.querySelector(".hqt__cell--amt");
      if (amt) {
        if (mode === "container") {
          var wq = row.found ? actWeight(row) * KG_TO_LB : 0;
          amt.innerHTML = wq >= GAUGE_MIN_LB ? fmtLb(wq * row.qty) + " lb" : '<span class="hqt__rfq">at quote</span>';
        } else if (actPriced(row)) amt.textContent = usd(actPrice(row) * row.qty);
        else amt.innerHTML = '<span class="hqt__rfq">RFQ</span>';
      }
      // availability depends on the quantity now, so re-badge the row too
      var av = rowEl && rowEl.querySelector(".hqt__cell--avail");
      if (av) av.innerHTML = pillHtml(leadInfo(row));
      if (mode === "container") renderGauge(); else renderTotals();
    });

    // OEM / Reliant / aftermarket segmented control: set the chosen spec.
    els.tbody.addEventListener("click", function (e) {
      var opt = e.target.closest("[data-hqt-alt-set]");
      if (!opt) return;
      var k = parseInt(opt.getAttribute("data-hqt-row"), 10);
      var want = opt.getAttribute("data-hqt-alt-set");
      if (want !== "oem" && want !== "rs" && want !== "am") want = "oem";
      if (state.rows[k].spec === want) return;
      state.rows[k].spec = want;
      render();
    });

    // ---- Actions ----
    els.priceBtn.addEventListener("click", priceIt);
    els.textarea.addEventListener("input", setPriceDisabled);

    // Back to the List (input) view. Rebuild part lines from current row state
    // (keeps qty edits made in the table), then put the prose/contact note lines
    // back so they aren't lost on a round-trip. Rows extracted FROM a note are
    // skipped — the note line still contains them and re-extracts on the next
    // Price-it (emitting both would double).
    function showListView() {
      var partLines = state.rows
        .filter(function (r) { return !r.fromNote; })
        .map(function (r) { return r.qty > 1 ? r.query + " " + r.qty : r.query; });
      els.textarea.value = partLines.concat(state.notes || []).join("\n");
      els.priceBtn.textContent = priceLabel;
      setPriceDisabled();
      setView(false);
      els.textarea.focus();
    }

    if (els.tabList) {
      els.tabList.addEventListener("click", function () {
        // Only act when coming FROM the quote view (avoid clobbering a manual
        // edit if the List tab is clicked while already on the list).
        if (els.input.hidden) showListView();
      });
    }
    if (els.tabQuote) {
      els.tabQuote.addEventListener("click", function () {
        if (els.tabQuote.disabled || !els.results.hidden) return;
        setView(true);
      });
    }

    // Get a quote: the same contact dialog and endpoint as the cart (app embed
    // cart_rfq.liquid + cart-rfq.js, exposed as window.FrankRFQ). One request
    // carries every row - matched parts by variant (honouring the OEM /
    // Reliant / aftermarket choice), unmatched numbers as "Not listed yet"
    // lines - plus the buyer's prose as notes and any destination picked in
    // the shipping estimator. The buyer lands on /pages/quote-received and
    // gets a confirmation email. Falls back to a mailto if the embed is off.
    els.quote.dataset.label = els.quote.textContent;
    els.quote.addEventListener("click", function () {
      var items = state.rows.map(function (r) {
        var vid = r.found ? actVariantId(r) : null;
        return vid
          ? { variantId: vid, sku: r.part + specSkuSuffix(r), title: oneLine(r.desc) + specNote(r), quantity: r.qty }
          : { sku: r.part, title: r.found ? oneLine(r.desc) : "Not listed yet", quantity: r.qty };
      });
      var contact = { notes: rfqNotes().join("\n") };
      if (els.country && els.country.value) {
        contact.country = els.country.value;
        if (els.province && !els.province.hidden && els.province.value) contact.province = els.province.value;
      }
      var opts = { items: items, contact: contact, source: "pad" };
      if (mode === "container") {
        // The load goes to us as `details`, current at the click; the notes
        // box stays the buyer's (2026-10-26: it used to be pre-typed with a
        // "Needed by:" blank and a load line that went stale on reopen).
        opts.source = "container";
        opts.details = gaugeSummary();
        opts.notesPlaceholder = "When you need the parts, and anything else we should know";
        opts.lede = "We reply with pricing for the whole container and the factory lead time. This is not an order and nothing is charged.";
        opts.submitLabel = "Request container pricing";
        opts.title = "Container pricing";
      }
      if (window.FrankRFQ && typeof window.FrankRFQ.open === "function" && window.FrankRFQ.open(opts)) {
        return;
      }
      var lines = state.rows.map(function (r) {
        return r.part + " x" + r.qty + specNote(r) + (r.found ? "" : " (not in catalog)");
      }).concat(rfqNotes());
      window.location.href = "mailto:" + email + "?subject=" + encodeURIComponent("Quote request - Frank Aggregate") +
        "&body=" + encodeURIComponent(["Hello,", "Please quote:", lines.join("\n")].join("\n\n"));
    });

    // Copy as TSV (+ HTML table fallback).
    var HEADERS = ["Part #", "Description", "Qty", "Unit price", "Line total", "Availability"];

    function buildTsv() {
      var t = totals();
      var lines = [HEADERS.join("\t")];
      state.rows.forEach(function (r) {
        lines.push(
          [r.part, oneLine(r.desc) + specNote(r), r.qty,
            actPriced(r) ? usd(actPrice(r)) : "", actPriced(r) ? usd(actPrice(r) * r.qty) : "RFQ",
            leadInfo(r).word].join("\t")
        );
      });
      rfqNotes().forEach(function (n) {
        lines.push(["Note", oneLine(n), "", "", "", ""].join("\t"));
      });
      lines.push(["", "", "", "", "Total weight", fmtWeight(weightLbTotal())].join("\t"));
      lines.push(["", "", "", "", t.live ? "Shipping (" + t.live.name + ")" : t.liftgate ? "Est. shipping (LTL)" : "Est. shipping (ground)", t.live ? usd(t.shipping) : usd0(t.shipping)].join("\t"));
      if (!t.live && t.inboundShipping > 0) lines.push(["", "", "", "", "Est. shipping from the manufacturer (special order)", usd0(t.inboundShipping)].join("\t"));
      if (t.savings > 0.5) lines.push(["", "", "", "", "Aftermarket saved", usd0(t.savings)].join("\t"));
      lines.push(["", "", "", "", "Estimated total", t.pricedCount ? usd(t.total) : "Quote on request"].join("\t"));
      return lines.join("\n");
    }

    function buildHtml() {
      var t = totals();
      var th = "padding:4px 8px;border:1px solid #cfd6df;background:#f2f5f8;text-align:left;font-family:Arial,sans-serif;font-size:12px;";
      var td = "padding:4px 8px;border:1px solid #cfd6df;font-family:Arial,sans-serif;font-size:12px;";
      var rows = state.rows
        .map(function (r) {
          return (
            "<tr><td style='" + td + "'>" + esc(r.part) + "</td><td style='" + td + "'>" +
            esc(oneLine(r.desc)) + esc(specNote(r)) +
            "</td><td style='" + td + "text-align:right'>" + r.qty + "</td><td style='" + td + "text-align:right'>" +
            (actPriced(r) ? usd(actPrice(r)) : "") + "</td><td style='" + td + "text-align:right'>" +
            (actPriced(r) ? usd(actPrice(r) * r.qty) : "RFQ") + "</td><td style='" + td + "'>" +
            esc(leadInfo(r).word) + "</td></tr>"
          );
        })
        .join("");
      var noteRows = rfqNotes()
        .map(function (n) {
          return "<tr><td style='" + td + "'><i>Note</i></td><td colspan='5' style='" + td + "'>" + esc(oneLine(n)) + "</td></tr>";
        })
        .join("");
      var foot =
        (t.noWeight ? "" : "<tr><td colspan='4'></td><td style='" + th + "'>Total weight</td><td style='" + td + "'>" + esc(fmtWeight(weightLbTotal())) + "</td></tr>") +
        "<tr><td colspan='4'></td><td style='" + th + "'>" + (t.live ? "Shipping (" + esc(t.live.name) + ")" : t.noWeight ? "Shipping" : t.liftgate ? "Est. shipping (LTL)" : "Est. shipping (ground)") + "</td><td style='" + td + "'>" + (t.live ? usd(t.shipping) : t.noWeight ? "with quote" : usd0(t.shipping)) + "</td></tr>" +
        (!t.live && t.inboundShipping > 0 ? "<tr><td colspan='4'></td><td style='" + th + "'>Est. shipping from the manufacturer (special order)</td><td style='" + td + "'>" + usd0(t.inboundShipping) + "</td></tr>" : "") +
        (t.savings > 0.5 ? "<tr><td colspan='4'></td><td style='" + th + "'>Aftermarket saved</td><td style='" + td + "'>" + usd0(t.savings) + "</td></tr>" : "") +
        "<tr><td colspan='4'></td><td style='" + th + "'>Estimated total</td><td style='" + td + "'><b>" + (t.pricedCount ? usd(t.total) : "Quote on request") + "</b></td></tr>";
      return (
        "<table style='border-collapse:collapse'><thead><tr>" +
        HEADERS.map(function (h) { return "<th style='" + th + "'>" + esc(h) + "</th>"; }).join("") +
        "</tr></thead><tbody>" + rows + noteRows + foot + "</tbody></table>"
      );
    }

    function flash(btn, label, done) {
      btn.textContent = label;
      if (done) btn.classList.add("hqt__act--done");
      clearTimeout(btn._t);
      btn._t = setTimeout(function () {
        btn.textContent = btn.dataset.label;
        btn.classList.remove("hqt__act--done");
      }, 1800);
    }

    els.copy.dataset.label = els.copy.textContent;
    els.copy.addEventListener("click", function () {
      if (!state.rows.length) return;
      var tsv = buildTsv();
      var done = function () { flash(els.copy, "Copied"); };
      try {
        if (navigator.clipboard && window.ClipboardItem) {
          navigator.clipboard
            .write([new ClipboardItem({
              "text/html": new Blob([buildHtml()], { type: "text/html" }),
              "text/plain": new Blob([tsv], { type: "text/plain" }),
            })])
            .then(done, function () { navigator.clipboard.writeText(tsv).then(done, done); });
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(tsv).then(done, done);
        } else {
          done();
        }
      } catch (e) {
        if (navigator.clipboard) navigator.clipboard.writeText(tsv).then(done, done);
      }
    });

    // Share: copy a link that reproduces this parts list. The list (+ shipping
    // destination) is saved server-side and the link is a short ?pad=<code> —
    // the inline ?rfq=SKU:QTY,... form got unwieldy past a few parts. If the
    // share endpoint is unreachable the inline form is the fallback, so the
    // button always yields a working link. Notes are excluded on purpose: they
    // can hold names/phone numbers, and PII never belongs in a URL.
    if (els.share) {
      els.share.dataset.label = els.share.textContent;
      els.share.addEventListener("click", function () {
        if (!state.rows.length) return;
        var dest = null;
        if (els.country && els.country.value) {
          dest = {
            country: els.country.value,
            province: els.province && !els.province.hidden ? els.province.value || "" : "",
            zip: els.zip ? els.zip.value || "" : "",
          };
        }
        var finish = function (url) {
          var done = function () { flash(els.share, "Link copied"); };
          if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, done);
          else done();
        };
        var inline = function () {
          var packed = state.rows
            .map(function (r) {
              var q = Math.min(999, r.qty); // 4+ digit qty would parse as a SKU on reload
              return r.query + (q > 1 ? ":" + q : "");
            })
            .join(",");
          // Commas/colons are legal raw in a query string; un-escaping them
          // keeps even the fallback link readable.
          var url = location.origin + location.pathname + "?rfq="
            + encodeURIComponent(packed).replace(/%2C/gi, ",").replace(/%3A/gi, ":");
          if (dest) url += "&dest=" + encodeURIComponent([dest.country, dest.province, dest.zip].join("~"));
          finish(url);
        };
        fetch(endpoint.replace("lookup.json", "pad-share.json"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: state.rows.map(function (r) { return { sku: r.query, qty: Math.min(999, r.qty) }; }),
            dest: dest,
          }),
        })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (data) {
            if (data && data.code) finish(location.origin + location.pathname + "?pad=" + data.code);
            else inline();
          })
          .catch(inline);
      });
    }

    // Cart-API items for the rows (honouring the row's OEM/Reliant/Aftermarket
    // selection). Shared by "Add all to cart" and the destination shipping
    // estimator. The lookup returns the variant id; the product .js fetch is
    // the fallback for a row that somehow lacks one.
    function resolveCartItems(rows) {
      return Promise.all(
        rows.map(function (r) {
          var vid = actVariantId(r);
          if (vid) return Promise.resolve({ id: Number(String(vid).split("/").pop()), quantity: r.qty });
          var wantSku = (r.part + specSkuSuffix(r)).toUpperCase();
          return fetch(actUrl(r) + ".js", { headers: { Accept: "application/json" } })
            .then(function (res) { return res.ok ? res.json() : null; })
            .then(function (product) {
              if (!product || !product.variants) return null;
              var match = product.variants.filter(function (v) {
                return String(v.sku || "").toUpperCase() === wantSku;
              })[0] || product.variants[0];
              return match ? { id: match.id, quantity: r.qty } : null;
            })
            .catch(function () { return null; });
        })
      ).then(function (items) { return items.filter(Boolean); });
    }

    // ---- Destination shipping estimator ----
    // Same app endpoint as the product page's "Shipping & customs" block
    // (/apps/machines/shipping-rates.json, draftOrderCalculate server-side),
    // but quoted for the WHOLE list in one calculation. The cheapest returned
    // rate replaces the heuristic estimate in the totals.
    if (els.dest && els.country && els.ratesBtn) {
      var rateNoteMsg = function (msg) {
        if (!els.rateNote) return;
        els.rateNote.textContent = msg || "";
        if (msg) els.rateNote.dataset.msg = "1";
        else delete els.rateNote.dataset.msg;
      };

      // country_option_tags carries each country's provinces in a data attr.
      els.country.addEventListener("change", function () {
        var opt = els.country.options[els.country.selectedIndex];
        var provinces = [];
        try { provinces = JSON.parse((opt && opt.dataset && opt.dataset.provinces) || "[]"); } catch (e) { provinces = []; }
        els.province.innerHTML = '<option value="">State / province</option>' + provinces
          .map(function (p) { return '<option value="' + esc(p[0]) + '">' + esc(p[1]) + "</option>"; })
          .join("");
        els.province.hidden = provinces.length === 0;
      });

      els.ratesBtn.dataset.label = els.ratesBtn.textContent;
      els.dest.addEventListener("submit", function (ev) {
        ev.preventDefault();
        var country = els.country.value;
        if (!country) { rateNoteMsg("Pick a country first."); return; }
        var quotable = state.rows.filter(function (r) { return r.found && actUrl(r); });
        if (!quotable.length) { rateNoteMsg("Nothing to quote yet."); return; }
        var sig = itemsSig();
        els.ratesBtn.disabled = true;
        els.ratesBtn.textContent = "Calculating…";
        rateNoteMsg("");

        resolveCartItems(quotable)
          .then(function (items) {
            if (!items.length) throw new Error("no variants resolved");
            return fetch(endpoint.replace("lookup.json", "shipping-rates.json"), {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({
                items: items.map(function (it) { return { variantId: it.id, quantity: it.quantity }; }),
                country: country,
                province: els.province && !els.province.hidden ? els.province.value || null : null,
                zip: els.zip ? els.zip.value || null : null,
              }),
            });
          })
          .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
          .then(function (out) {
            els.ratesBtn.disabled = false;
            els.ratesBtn.textContent = els.ratesBtn.dataset.label;
            var rates = out.ok && out.data && out.data.rates ? out.data.rates : null;
            if (!rates || !rates.length) {
              state.liveRate = null;
              rateNoteMsg((out.data && out.data.error) || "No rates for that destination. We quote it with your RFQ instead.");
              renderTotals();
              return;
            }
            rates.sort(function (a, b) { return Number(a.price) - Number(b.price); });
            var best = rates[0];
            var dest = [els.zip && els.zip.value, country].filter(Boolean).join(", ");
            state.liveRate = {
              sig: sig,
              name: best.name,
              amount: Number(best.price),
              note: "Live rate to " + dest + (rates.length > 1 ? " · " + (rates.length - 1) + " more option" + (rates.length > 2 ? "s" : "") + " at checkout" : "")
                + (quotable.some(function (r) { return r.inboundShip === true; }) ? " · includes the leg from the manufacturer for special-order parts" : ""),
            };
            rateNoteMsg("");
            renderTotals();
          })
          .catch(function () {
            els.ratesBtn.disabled = false;
            els.ratesBtn.textContent = els.ratesBtn.dataset.label;
            rateNoteMsg("Could not get rates. We quote shipping with your RFQ instead.");
          });
      });
    }

    // Add all found rows to the real Shopify cart, honouring each row's OEM/AFTMKT
    // selection.
    function resetAddAll() {
      if (!els.addAll) return; // container mode has no cart action
      els.addAll.classList.remove("hqt__act--done");
      // Any found row with a product URL can go to cart, INCLUDING quote-only
      // ($0) parts (the cart's Get a quote handles them). Only not-found rows
      // are excluded; with nothing addable the button hides and Get a quote
      // is the one action.
      var addable = state.rows.filter(function (r) { return r.found && actUrl(r); });
      els.addAll.disabled = false;
      els.addAll.hidden = !addable.length;
      els.addAll.textContent = els.addAll.dataset.label;
    }

    if (els.addAll) els.addAll.dataset.label = els.addAll.textContent;
    if (els.addAll) els.addAll.addEventListener("click", function () {
      var addable = state.rows.filter(function (r) { return r.found && actUrl(r); });
      if (!addable.length) return;
      els.addAll.disabled = true;
      els.addAll.textContent = "Adding…";

      resolveCartItems(addable)
        .then(function (valid) {
          if (!valid.length) throw new Error("no variants resolved");
          // Ask the Cart API to also return the re-rendered cart-icon-bubble
          // section so we can refresh the header count in the same round-trip.
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: valid, sections: ["cart-icon-bubble"], sections_url: window.location.pathname }),
          });
        })
        .then(function (res) { if (!res.ok) throw new Error("cart add failed"); return res.json(); })
        .then(function (data) {
          els.addAll.disabled = false;
          flash(els.addAll, "✓ Added", true);
          refreshCartBubble(data && data.sections && data.sections["cart-icon-bubble"]);
        })
        .catch(function () { resetAddAll(); });
    });

    // Refresh Dawn's header cart count. The header's <a id="cart-icon-bubble">
    // holds the icon + count markup directly, and the cart-icon-bubble section
    // renders exactly that inner content — so swap it straight in.
    function refreshCartBubble(html) {
      var bubble = document.getElementById("cart-icon-bubble");
      if (!bubble) return;
      if (html != null) {
        bubble.innerHTML = html;
        return;
      }
      fetch("/?sections=cart-icon-bubble")
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (data && data["cart-icon-bubble"] != null) bubble.innerHTML = data["cart-icon-bubble"];
        })
        .catch(function () {});
    }

    // ---- Image intake: the camera tile (Claude Design 1a) ----
    // A stateful drop/upload/camera target beside the textarea. The app reads
    // part numbers out of the image (/apps/machines/ocr.json, Claude vision)
    // and the lines land in the textarea for the buyer to CONFIRM — never
    // auto-priced. Client-side downscale to <=1200px JPEG cuts image tokens
    // ~45% with no extraction loss (benchmarked) and normalizes formats.
    // Dragging anywhere over the hero lights the tile; paste works globally.
    if (els.file && els.textarea && els.tile) {
      var scanBusy = false;
      var tileTimer = null;
      var coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

      var TILE_IDLE = coarse
        ? ["Snap a photo", "Camera opens — we read the part numbers off your list"]
        : ["Scan a parts list", "Click to upload, drag a photo, or paste a screenshot"];

      // One place sets the tile's class, copy, and scan-line visibility.
      var setTile = function (state, title, sub) {
        clearTimeout(tileTimer);
        els.tile.classList.remove("is-drag", "is-busy", "is-done", "is-error");
        if (state !== "idle") els.tile.classList.add("is-" + state);
        if (els.tileTitle) els.tileTitle.textContent = title;
        if (els.tileSub) els.tileSub.textContent = sub;
        if (els.scanbar) els.scanbar.hidden = state !== "busy";
      };
      var tileIdle = function () { setTile("idle", TILE_IDLE[0], TILE_IDLE[1]); };
      var tileRevert = function (ms) { tileTimer = setTimeout(tileIdle, ms); };
      tileIdle();

      var fileToJpeg = function (file) {
        return new Promise(function (resolve, reject) {
          var url = URL.createObjectURL(file);
          var img = new Image();
          img.onload = function () {
            try {
              var scale = Math.min(1, 1200 / Math.max(img.naturalWidth, img.naturalHeight));
              var w = Math.max(1, Math.round(img.naturalWidth * scale));
              var h = Math.max(1, Math.round(img.naturalHeight * scale));
              var canvas = document.createElement("canvas");
              canvas.width = w;
              canvas.height = h;
              canvas.getContext("2d").drawImage(img, 0, 0, w, h);
              URL.revokeObjectURL(url);
              resolve(canvas.toDataURL("image/jpeg", 0.85).split(",")[1]);
            } catch (e) { URL.revokeObjectURL(url); reject(new Error("decode failed")); }
          };
          img.onerror = function () { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
          img.src = url;
        });
      };

      var tileError = function (sub) {
        setTile("error", "Couldn't read that", sub || ("try a clearer photo — " + (coarse ? "tap" : "click") + " to retry"));
        tileRevert(4000);
      };

      var scan = function (file) {
        if (!file || scanBusy) return;
        if (file.type && file.type.indexOf("image/") !== 0) { tileError("that file isn't an image"); return; }
        scanBusy = true;
        setTile("busy", "Reading part numbers…", "usually a few seconds");
        fileToJpeg(file)
          .then(function (b64) {
            return fetch(endpoint.replace("lookup.json", "ocr.json"), {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ image: b64, media_type: "image/jpeg" }),
            });
          })
          .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, d: d }; }); })
          .then(function (out) {
            var lines = out.ok && out.d && out.d.lines ? out.d.lines : null;
            if (!lines) throw new Error((out.d && out.d.error) || "read failed");
            if (!lines.length) { tileError("no part numbers found in that image"); return; }
            var existing = els.textarea.value.replace(/\s+$/, "");
            els.textarea.value = (existing ? existing + "\n" : "") + lines.join("\n");
            setPriceDisabled();
            setTile("done", "Added " + lines.length + " line" + (lines.length === 1 ? "" : "s"), "check them, then press Price it");
            tileRevert(3200);
          })
          .catch(function (e) {
            tileError(e && e.message === "decode failed"
              ? "couldn't open that image — try a JPG, PNG, or screenshot"
              : (e && e.message && e.message.length < 80 && e.message !== "read failed" ? e.message : null));
          })
          .then(function () {
            scanBusy = false;
            if (els.file) els.file.value = "";
          });
      };

      els.file.addEventListener("change", function () { scan(els.file.files && els.file.files[0]); });

      // The whole hero is the drop zone: dragging anywhere lights the tile.
      root.addEventListener("dragover", function (ev) {
        ev.preventDefault();
        if (!scanBusy && !els.tile.classList.contains("is-drag")) {
          setTile("drag", "Drop it here", "We'll read the part numbers");
        }
      });
      root.addEventListener("dragleave", function (ev) {
        if (els.tile.classList.contains("is-drag") && (ev.target === root && !root.contains(ev.relatedTarget) || !ev.relatedTarget)) {
          tileIdle();
        }
      });
      root.addEventListener("drop", function (ev) {
        ev.preventDefault();
        var f = ev.dataTransfer && ev.dataTransfer.files && ev.dataTransfer.files[0];
        if (els.tile.classList.contains("is-drag")) tileIdle();
        if (f) scan(f);
      });

      // Paste works globally (screenshots / snips) — image items only, so
      // normal text paste anywhere is untouched.
      window.addEventListener("paste", function (ev) {
        var items = ev.clipboardData && ev.clipboardData.items;
        if (!items) return;
        for (var i = 0; i < items.length; i++) {
          if (items[i].type && items[i].type.indexOf("image/") === 0) {
            ev.preventDefault();
            scan(items[i].getAsFile());
            return;
          }
        }
      });
    }

    setPriceDisabled();

    // A shared link (built by the Share button) reproduces a parts list:
    // prefill the pad and price it straight away. ?pad=<code> resolves the
    // list (and destination) from the pad-share endpoint — the short form.
    // Legacy inline ?rfq=SKU:QTY,... links keep working forever, with their
    // optional ?dest= companion (country~province~zip).
    function applySharedDest(bits) {
      if (!bits || !els.country) return;
      var wantCountry = (bits[0] || "").trim();
      var hasCountry = Array.prototype.some.call(els.country.options, function (o) {
        return o.value === wantCountry;
      });
      if (!hasCountry || !wantCountry) return;
      els.country.value = wantCountry;
      els.country.dispatchEvent(new Event("change", { bubbles: true }));
      if (els.province && !els.province.hidden && bits[1]) els.province.value = bits[1];
      if (els.zip && bits[2]) els.zip.value = bits[2];
      state.pendingAutoRates = true;
    }
    function applySharedList(pairs) {
      var lines = pairs
        .slice(0, 200)
        .map(function (p) {
          var sku = String(p.sku || "").trim();
          var q = Math.min(999, parseInt(p.qty, 10) || 1);
          if (!sku) return "";
          return q > 1 ? sku + " " + q : sku;
        })
        .filter(Boolean);
      if (!lines.length) return;
      els.textarea.value = lines.join("\n");
      setPriceDisabled();
      priceIt();
    }
    try {
      var params = new URLSearchParams(window.location.search);
      var padCode = params.get("pad");
      var shared = params.get("rfq");
      var sharedDest = params.get("dest");
      if (padCode && /^[A-Za-z0-9]{6,12}$/.test(padCode)) {
        fetch(endpoint.replace("lookup.json", "pad-share.json") + "?code=" + encodeURIComponent(padCode), {
          headers: { Accept: "application/json" },
        })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (data) {
            if (!data || !data.items) return;
            if (data.dest && data.dest.country) {
              applySharedDest([data.dest.country, data.dest.province || "", data.dest.zip || ""]);
            }
            applySharedList(data.items);
          })
          .catch(function () { /* dead share link — leave the pad empty */ });
      } else {
        if (sharedDest) applySharedDest(sharedDest.slice(0, 200).split("~"));
        if (shared) {
          applySharedList(
            shared
              .slice(0, 4000)
              .split(",")
              .slice(0, 200)
              .map(function (entry) {
                var bits = entry.split(":");
                return { sku: bits[0] || "", qty: parseInt(bits[1], 10) || 1 };
              })
          );
        }
      }
    } catch (e) {
      /* malformed share link — leave the pad empty */
    }
  }

  function initAll(ctx) {
    var roots = (ctx || document).querySelectorAll("[data-hqt]");
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", function (e) { initAll(e.target); });

  // Deep-link overshoot fix. On a COLD load the browser can jump to
  // #quote-tool before the hero's background image, web fonts and JS-built pad
  // settle, overshooting the anchor and landing the visitor further down the
  // page (the "/#quote-tool" CTA appeared to scroll to the bottom). Once the
  // page has settled, re-assert the scroll to the anchor — but never fight a
  // visitor who has already started scrolling, and only correct a genuine
  // overshoot. Warm loads land correctly and are left untouched.
  (function fixQuoteAnchorScroll() {
    if (location.hash !== "#quote-tool") return;
    var moved = false;
    var flag = function () { moved = true; };
    window.addEventListener("wheel", flag, { passive: true, once: true });
    window.addEventListener("touchmove", flag, { passive: true, once: true });
    var reassert = function () {
      if (moved) return;
      var a = document.getElementById("quote-tool");
      if (a && Math.abs(a.getBoundingClientRect().top) > 8) a.scrollIntoView();
    };
    var run = function () {
      requestAnimationFrame(reassert);
      setTimeout(reassert, 300); // catch a late image/layout settle
    };
    if (document.readyState === "complete") run();
    else window.addEventListener("load", run, { once: true });
  })();
})();
